<template>
  <section>
    <b-row
      v-if="!isXml && !readOnly"
      class="mb-1"
    >
      <b-col class="d-flex justify-content-end">
        <e-button
          :text="$t('Adicionar pagamento')"
          variant="primary"
          @click="onAddPayment"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col md="12">
        <FormulateForm ref="formulatePayments">
          <b-table
            ref="productTable"
            show-empty
            striped
            class="bordered"
            :fields="fields"
            :items="payments"
          >
            <template #cell(paymentMethod)="row">
              <div class="d-flex align-items-center">
                <div
                  class="flex-grow-1"
                  :class="{ 'mr-2': isCardPaymentMethod(row.item.paymentMethodId) || isPixPaymentMethod(row.item.paymentMethodId) }"
                >
                  <!-- validation-messages é necessário pois o formulate entende label vazia como null -->
                  <e-payment-method
                    :id="`invoice-payments-payment_type-${row.index}`"
                    v-model="row.item.paymentMethodId"
                    :is-read-only="readOnly"
                    :placeholder="$t('Clique para selecionar')"
                    label=""
                    required
                    :validation-messages="{
                      required: $t('Este campo é obrigatório'),
                    }"
                    :flag="getPaymentMethodFlag"
                    @input="handleSwitchChange(row.item)"
                  />
                </div>
                <div class="mb-2">
                  <FormulateInput
                    v-if="(isCardPaymentMethod(row.item.paymentMethodId)
                      || isPixPaymentMethod(row.item.paymentMethodId)
                    ) && !readOnly"
                    v-model="row.item.isManual"
                    :disabled="isCardPaymentMethod(row.item.paymentMethodId)"
                    :instruction="$t('Ative para pagamento Manual e mantenha desativado para pagamento POS')"
                    type="switch"
                    label="Manual?"
                    @input="handleSwitchChange(row.item, $event)"
                  />
                </div>
              </div>
              <div
                v-if="isNsuRequired(row.item)"
                class="flex-grow-1"
              >
                <FormulateInput
                  v-if="!readOnly"
                  key="nsu"
                  v-model.trim="row.item.nsu"
                  v-bind="$attrs"
                  type="number"
                  label=""
                  class="required"
                  validation="required|matches"
                  :validation-rules="{
                    matches: ({ value }) => /^(?!0+$)\d+$/.test(value),
                  }"
                  :validation-messages="{
                    required: $t('O NSU é obrigatório para a forma de pagamento selecionada'),
                    matches: $t('O NSU deve ser um número válido'),
                  }"
                  :placeholder="$t('NSU')"
                />
                <FormulateInput
                  v-else-if="readOnly && row.item.nsu"
                  :value="`NSU: ${row.item.nsu}`"
                  v-bind="$attrs"
                  type="label"
                />
              </div>
              <div
                v-else-if="isPixPaymentMethod(row.item.paymentMethodId) && row.item.isManual"
                class="flex-grow-1"
              >
                <FormulateInput
                  v-if="!readOnly"
                  key="cpfCnpj"
                  v-model.trim="row.item.cpfCnpj"
                  v-bind="$attrs"
                  type="text-mask"
                  :mask="['###.###.###-##', 'XX.XXX.XXX/XXXX-##']"
                  label=""
                  :validation="[
                    'cpfcnpj'
                  ]"
                  :validation-rules="{
                    cpfcnpj: ({ value }) => !value || validateTitularDocument(value)
                  }"
                  :validation-messages="{
                    cpfcnpj: $t('Digite um CPF ou CNPJ válido')
                  }"
                  :placeholder="$t('Documento do titular')"
                />
                <FormulateInput
                  v-else-if="readOnly && row.item.cpfCnpj"
                  :value="`Documento do titular: ${row.item.cpfCnpj}`"
                  v-bind="$attrs"
                  type="label"
                />
              </div>

              <div
                v-if="showE2EIdPix(row.item)"
                class="flex-grow-1"
              >
                <FormulateInput
                  :value="`Id da transação (E2E Id): ${row.item.authorizationApprovalCode}`"
                  v-bind="$attrs"
                  type="label"
                />
              </div>
            </template>

            <template #cell(paymentValue)="row">
              <template v-if="!readOnly">
                <FormulateInput
                  v-if="!canPaymentValueBeZero(row.item)"
                  :id="`invoice-payments-payment_value-${row.index}`"
                  v-model="row.item.paymentValue"
                  type="text-number"
                  currency="R$"
                  :precision="3"
                  class="text-right"
                  validation="required|min:0.01"
                  :validation-messages="{
                    required: $t('Este campo é obrigatório'),
                    min: $t('Este campo deve ser maior que 0.01'),
                  }"
                />
                <FormulateInput
                  v-else
                  :id="`invoice-payments-payment_value-${row.index}`"
                  v-model="row.item.paymentValue"
                  type="text-number"
                  currency="R$"
                  :precision="3"
                  class="text-right"
                  validation="required"
                  :validation-messages="{ required: $t('Este campo é obrigatório') }"
                />
              </template>
              <span v-else>{{ row.item.paymentValue | currency }}</span>
            </template>

            <template #cell(changeValue)="row">
              <FormulateInput
                v-if="!readOnly"
                :id="`invoice-payments-payment_change_value-${row.index}`"
                v-model="row.item.changeValue"
                type="text-number"
                currency="R$"
                :precision="3"
                class="text-right"
              />
              <span v-else>{{ row.item.changeValue | currency }}</span>
            </template>
            <template #cell(dueDate)="row">
              <FormulateInput
                v-if="!readOnly"
                :id="`invoice-payments-payment_duedate-${row.index}`"
                v-model="row.item.dueDate"
                type="datepicker"
                class="text-right"
              />
              <span v-else>{{ row.item.dueDate | date }}</span>
            </template>

            <template #cell(action)="row">
              <e-grid-actions
                :show-update="false"
                :show-delete="true"
                @delete="onRemovePayment(row.item)"
              />
            </template>
          </b-table>
        </FormulateForm>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import { BTable, BRow, BCol } from 'bootstrap-vue'
import { accountTypes, paymentTypes } from '@/mixins'
import EGridActions from '@/views/components/EGridActions.vue'
import EButton from '@/views/components/EButton.vue'
import EPaymentMethod from '@/views/components/inputs/EPaymentMethod.vue'
import { validateBr } from 'js-brasil'

export default {
  components: { BTable, BRow, BCol, EGridActions, EButton, EPaymentMethod },
  mixins: [paymentTypes, accountTypes],

  props: {
    isXml: {
      type: Boolean,
      default: false,
    },
    invoice: {
      type: Object,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {}
  },

  computed: {
    getPaymentMethodFlag() {
      if (this.readOnly) return 'all'

      const options = {
        Return: 'invoiceReturn',
        Sale: 'invoiceSale',
        Transference: 'invoiceTransference',
        StockLosses: 'invoiceStockLosses',
        ProductBonus: 'invoiceProductBonus',
        StoreLending: 'invoiceStoreLending',
      }
      return options[this.invoice?.invoiceCategory] || 'all'
    },

    payments() {
      return this.invoice?.payments?.filter(p => !p.isDeleted) || []
    },
    paymentMethod() {
      const [firstPayment] = this.payments
      return firstPayment?.paymentMethod?.name || { name: '-' }
    },

    fields() {
      return [
        {
          label: this.$t('Ação'),
          key: 'action',
          class: 'text-center',
          thStyle: { width: '100px' },
          hide: this.isXml || this.readOnly,
        },
        {
          label: this.$t('Forma de Pagamento'),
          key: 'paymentMethod',
          class: 'text-center',
          thStyle: 'width: 500px;',
        },
        {
          label: this.$t('Valor'),
          key: 'paymentValue',
          tdClass: 'text-right',
          thStyle: 'width: 180px;',
        },
        {
          label: this.$t('Data de Vencimento'),
          key: 'dueDate',
          tdClass: 'text-right',
          thStyle: 'width: 150px;',
          formatter: value => this.$options.filters.date(value),
        },
        {
          label: this.$t('Troco'),
          key: 'changeValue',
          tdClass: 'text-right',
          thStyle: 'width: 180px;',
        },
      ].filter(f => !f.hide)
    },
  },

  mounted() {},

  methods: {
    onAddPayment() {
      this.$emit('add-payment')
    },

    /* eslint-disable no-param-reassign */
    handleSwitchChange(item, value = null) {
      if (this.isCardPaymentMethod(item.paymentMethodId)) {
        item.isManual = false
        item.cpfCnpj = null
      } else if (this.isPixPaymentMethod(item.paymentMethodId)) {
        item.isManual = value || false
        item.nsu = null
      } else {
        item.isManual = null
        item.nsu = null
        item.cpfCnpj = null
      }
    },
    /* eslint-enable no-param-reassign */

    isCardPaymentMethod(paymentMethodId){
      return this.isDebitCardPaymentMethod(paymentMethodId)
        || this.isCreditCardPaymentMethod(paymentMethodId)
    },

    async onRemovePayment(payment) {
      if (
        await this.confirm({
          icon: 'question',
          text: this.$t('Tem certeza que deseja remover este pagamento?'),
        })
      ) {
        this.$emit('remove-payment', payment)
      }
    },

    isNsuRequired(item) {
      if (this.isPixPaymentMethod(item.paymentMethodId)) {
        return !item.isManual
      }

      return this.isCardPaymentMethod(item.paymentMethodId)
    },
    showE2EIdPix(item) {
      return this.isPixPaymentMethod(item.paymentMethodId) && item.authorizationApprovalCode
    },

    validateTitularDocument(documentTitular) {
      if(!validateBr.cpfcnpj(documentTitular || '')) {
        return false
      }

      return true
    },

    validate() {
      this.$refs.formulatePayments.showErrors()
      return this.$refs.formulatePayments.hasErrors
    },

    canPaymentValueBeZero(item) {
      const withoutPayment = this.getPaymentMethodByMethod(this.paymentTypeEnum.WITHOUT_PAYMENT)
      return item?.paymentMethodId === String(withoutPayment?.id)
    },
  },
}
</script>

<style scoped></style>
